import * as Yup from "yup";

import { defaultCompanyForm } from "../forms/DefaultFormData";
import { ICompanyFormDataType } from "../forms/FormDataTypes";
import { CompanyValidationSchema } from "../forms/validationSchemas/CompanyValidationSchema";
import { MAX_BULK_ROWS, parseCsv } from "./PropertyCsvHelpers";

const FIXED_COLUMNS = [
  "companyKey",
  "companyName",
  "address",
  "suite",
  "city",
  "state",
  "zip",
  "website",
] as const;

export interface IBulkCompanyRow {
  line: number;
  companyKey: string;
  formData: ICompanyFormDataType;
  errors: string[];
}

export interface IValidateCompanyCsvResult {
  fileErrors: string[];
  rows: IBulkCompanyRow[];
}

const csvCell = (value: string): string => {
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
};

const csvLine = (cells: string[]): string => cells.map(csvCell).join(",");

const isBlank = (value: string | undefined) => !value || value.trim().length === 0;

/**
 * Builds a downloadable CSV template for the optional "bulk add companies"
 * step. No lookups involved — CompanyForm has no dropdowns — so unlike
 * buildPropertyCsvTemplate this needs no API data and no gating.
 */
export const buildCompanyCsvTemplate = (): string => {
  const header = [...FIXED_COLUMNS];
  const exampleRow = [
    "HQ",
    "Example Holdings LLC",
    "123 Main St",
    "",
    "Brooklyn",
    "NY",
    "11201",
    "",
  ];
  const lines = [csvLine(header), csvLine(exampleRow)];
  return `﻿${lines.join("\r\n")}\r\n`;
};

/**
 * Parses + validates a Companies CSV against the same rules the single
 * Company form uses (CompanyValidationSchema), plus the companyKey checks
 * that only make sense in a CSV context: present and unique per file, since
 * property rows use it to route to the right company.
 */
export const validateCompanyCsv = (text: string): IValidateCompanyCsvResult => {
  const table = parseCsv(text);
  if (table.length === 0) {
    return { fileErrors: ["The companies CSV file is empty."], rows: [] };
  }

  const header = table[0];
  const headerLower = header.map((h) => h.toLowerCase().trim());
  const dataRows = table.slice(1);

  const fileErrors: string[] = [];

  const requiredMissing = FIXED_COLUMNS.filter(
    (col) => !headerLower.includes(col.toLowerCase())
  );
  if (requiredMissing.length > 0) {
    fileErrors.push(`Missing required column(s): ${requiredMissing.join(", ")}`);
  }

  const knownColumns = new Set(FIXED_COLUMNS.map((c) => c.toLowerCase()));
  const unknownColumns = headerLower.filter((h) => h.length > 0 && !knownColumns.has(h));
  if (unknownColumns.length > 0) {
    fileErrors.push(`Unrecognized column(s): ${unknownColumns.join(", ")}`);
  }

  if (dataRows.length === 0) {
    fileErrors.push("The companies CSV file has no data rows.");
  }
  if (dataRows.length > MAX_BULK_ROWS) {
    fileErrors.push(
      `Too many rows (${dataRows.length}). Bulk upload supports at most ${MAX_BULK_ROWS} companies per file.`
    );
  }

  if (fileErrors.length > 0) {
    return { fileErrors, rows: [] };
  }

  const colIndex = (name: string) => headerLower.indexOf(name.toLowerCase());
  const cell = (rowValues: string[], name: string) => (rowValues[colIndex(name)] ?? "").trim();

  const seenKeys = new Map<string, number>();

  const rows: IBulkCompanyRow[] = dataRows.map((rowValues, rowIdx) => {
    const errors: string[] = [];
    const line = rowIdx + 2; // +1 for header, +1 for 1-based line numbers

    const companyKey = cell(rowValues, "companyKey");
    if (isBlank(companyKey)) {
      errors.push("companyKey is required for every company row");
    } else {
      const keyLower = companyKey.toLowerCase();
      const firstLine = seenKeys.get(keyLower);
      if (firstLine !== undefined) {
        errors.push(`companyKey "${companyKey}" is already used on row ${firstLine}`);
      } else {
        seenKeys.set(keyLower, line);
      }
    }

    const formData: ICompanyFormDataType = {
      ...defaultCompanyForm,
      companyName: cell(rowValues, "companyName"),
      address: cell(rowValues, "address"),
      suite: cell(rowValues, "suite"),
      city: cell(rowValues, "city"),
      state: cell(rowValues, "state"),
      zip: cell(rowValues, "zip"),
      website: cell(rowValues, "website"),
    };

    try {
      CompanyValidationSchema.validateSync(formData, { abortEarly: false });
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        errors.push(...err.inner.map((e) => e.message));
      } else {
        errors.push("Validation failed for this row.");
      }
    }

    return { line, companyKey, formData, errors };
  });

  return { fileErrors: [], rows };
};
